"use client";

import React, { memo, useMemo } from "react";
// import Image from "next/image";
import SmartImage from "~/components/reusables/SmartImage";

// --- Types ---

interface QuoteSectionProps {
  data: {
    quote?: string;
    authorName?: string;
    authorTitle?: string;
    companyLogo?: {
      url: string;
      alternativeText?: string;
    };
    background?: {
      id: number;
      gradientType: string;
      colorFrom: string;
      colorTo: string;
    };
  };
}

// --- Main Component ---

const QuoteSection = memo(({ data }: QuoteSectionProps) => {
  // Logic to determine the background style remains identical to preserve design
  const getBackgroundStyle = () => {
    if (data?.background) {
      const { gradientType, colorFrom, colorTo } = data.background;
      const isRadial = gradientType?.toLowerCase() === "radial";
      const gradientFunction = isRadial ? "radial-gradient" : "linear-gradient";
      const direction = isRadial ? "circle" : "to bottom right";

      return {
        background: `${gradientFunction}(${direction}, ${colorFrom}, ${colorTo})`,
        backgroundSize: 'cover'
      };
    }

    return { 
      backgroundImage: "url('data:image/svg+xml;utf8,<svg viewBox=\'0 0 2050 627\' xmlns=\'http://www.w3.org/2000/svg\' preserveAspectRatio=\'none\'><rect x=\'0\' y=\'0\' height=\'100%\' width=\'100%\' fill=\'url(%23grad)\' opacity=\'1\'/><defs><radialGradient id=\'grad\' gradientUnits=\'userSpaceOnUse\' cx=\'0\' cy=\'0\' r=\'10\' gradientTransform=\'matrix(163.51 78.41 -43.812 292.63 29.028 -332.66)\'><stop stop-color=\'rgba(0,81,173,1)\' offset=\'0\'/><stop stop-color=\'rgba(0,135,204,1)\' offset=\'0.5\'/><stop stop-color=\'rgba(0,188,235,1)\' offset=\'1\'/></radialGradient></defs></svg>')", 
      backgroundSize: 'cover' 
    };
  };

  const backgroundStyle = useMemo(() => {
    if (!data?.background?.gradientType) return undefined;
      return getBackgroundStyle();
  }, [data?.background]);

  return (
    <section className="relative w-full py-12 lg:py-16 overflow-hidden flex justify-center">
      {/* Dynamic Background Layer */}
      <div 
        aria-hidden="true" 
        className="absolute inset-0 pointer-events-none" 
        style={backgroundStyle}
      />
      
      <div className="container mx-auto px-6 relative z-10 flex justify-center">
        <blockquote className="bg-white p-6 md:px-6 md:py-6 lg:px-16 lg:py-12 max-w-5xl border border-[#b9b9b9] shadow-2xl">
          
          {/* Company Logo - Fixed to use direct S3 URL */}
          {data?.companyLogo?.url && (
            <div className="relative w-36 h-16 mb-8">
              <SmartImage
                src={data.companyLogo.url}
                alt={"Company Logo"}
                className="object-contain object-left"
                fill
              />
            </div>
          )}

          {/* Quote Text */}
          <p className="font-serif text-md md:text-xl text-black leading-relaxed font-normal">
            {data?.quote}
          </p>

          {/* Author Attribution */}
          <cite className="not-italic text-black pt-6 block">
            <span className="font-bold text-md">{data?.authorName}</span>
            {data?.authorTitle && (
              <>
                , <span className="text-md font-light">{data?.authorTitle}</span>
              </>
            )}
          </cite>
        </blockquote>
      </div>
    </section>
  );
});

QuoteSection.displayName = "QuoteSection";

export default QuoteSection;